import { useEffect, useState } from "react";
import {
  Badge, Box, Button, HStack, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter,
  ModalHeader, ModalOverlay, Switch, Table, Tbody, Td, Text, Th, Thead, Tr,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import HelpIcon from "./HelpIcon";
import { PurposeType, WorkflowId } from "../../contexts/constants";
import { parseViewRestriction, serializeViewRestriction, ScreenUsageMap, SimulationUsageId } from "../../utils/viewRestriction";

interface ViewRestrictionModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** 現在の制限設定（projectBasicInfo.screenViewRestriction） */
  value: string | null | undefined;
  /** 基本情報フォームの入力中の目的（Simulation の表示名に使う） */
  purpose: string;
  isReadOnly: boolean;
  onConfirm: (json: string | null) => void;
}

const SECTION_KEY = "features.InvestmentScreening.InvestmentScreeningDetail.section.";
const BASIC_INFO_KEY = "features.InvestmentScreening.BasicInfo.";

/** Simulation は1項目で 10・11 の両方を保存する */
const SIMULATION_SCREEN_IDS = [WorkflowId.SimulationNewProduct, WorkflowId.SimulationProductivityImprovement];

/** 画面閲覧制限の設定モーダル（営業担当に対して制限する画面を選ぶ。スイッチ ON＝閲覧可、OFF＝閲覧不可） */
const ViewRestrictionModal = ({ isOpen, onClose, value, purpose, isReadOnly, onConfirm }: ViewRestrictionModalProps) => {
  const { t } = useTranslation();
  /** 制限する（閲覧不可の）画面ID */
  const [selected, setSelected] = useState<string[]>([]);

  // 開くたびに現在の設定で初期化する（確定せずに閉じた変更は破棄）
  useEffect(() => {
    if (isOpen) {
      setSelected(parseViewRestriction(value)?.sales ?? []);
    }
  }, [isOpen, value]);

  const simulationLabel = purpose === PurposeType.NewProduct.code
    ? t(`${SECTION_KEY}simulationNewProduct`)
    : t(`${SECTION_KEY}simulationProductivityImprovement`);

  /** 画面ID（または Simulation）→ 表示名 */
  const screenName = (screenId: string): string => {
    switch (screenId) {
      case WorkflowId.ProductionProcess: return t(`${SECTION_KEY}productionProcess`);
      case WorkflowId.Equipment: return t(`${SECTION_KEY}equipment`);
      case WorkflowId.ProfitAndLoss: return t(`${SECTION_KEY}cost`);
      case WorkflowId.ProjectProfitablityStatement: return t(`${SECTION_KEY}projectProfitability`);
      case WorkflowId.ReviewBusiness: return t(`${SECTION_KEY}reviewBusiness`);
      case SimulationUsageId:
      case WorkflowId.SimulationNewProduct:
      case WorkflowId.SimulationProductivityImprovement:
        return simulationLabel;
      default: return screenId;
    }
  };

  // モーダルの項目（screenIds は制限時に保存する画面ID）
  const items: { key: string; screenIds: string[]; isReviewOnly?: boolean }[] = [
    { key: WorkflowId.ProductionProcess, screenIds: [WorkflowId.ProductionProcess] },
    { key: WorkflowId.Equipment, screenIds: [WorkflowId.Equipment] },
    { key: WorkflowId.ProfitAndLoss, screenIds: [WorkflowId.ProfitAndLoss] },
    { key: WorkflowId.ProjectProfitablityStatement, screenIds: [WorkflowId.ProjectProfitablityStatement] },
    { key: SimulationUsageId, screenIds: SIMULATION_SCREEN_IDS },
    { key: WorkflowId.ReviewBusiness, screenIds: [WorkflowId.ReviewBusiness], isReviewOnly: true },
  ];

  const setRestricted = (screenIds: string[], restricted: boolean) => {
    setSelected((prev) => restricted
      ? [...prev.filter((id) => !screenIds.includes(id)), ...screenIds]
      : prev.filter((id) => !screenIds.includes(id)));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      {/* 注意文と画面名が折り返さない幅にする */}
      <ModalContent maxW="720px">
        <ModalHeader mr={5}>{t(`${BASIC_INFO_KEY}modal.viewRestrictionTitle`)}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text mb={1} fontSize="sm" color="red.600">
            {t(`${BASIC_INFO_KEY}help.viewRestrictionReportNotice`)}
          </Text>
          <Text mb={4} fontSize="sm" color="red.600">
            {t(`${BASIC_INFO_KEY}help.viewRestrictionCopyNotice`)}
          </Text>
          <Box borderWidth="1px" borderRadius="md" overflow="hidden" borderColor="gray.200">
            <Table size="md">
              <Thead>
                <Tr>
                  <Th bgColor="gray.100" borderRight="1px solid" borderColor="gray.200" width="60%">
                    {t(`${BASIC_INFO_KEY}label.viewRestrictionScreen`)}
                  </Th>
                  <Th bgColor="gray.100">{t(`${BASIC_INFO_KEY}label.viewRestrictionSales`)}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {items.map((item) => {
                  const usages = ScreenUsageMap[item.key];
                  const isRestricted = item.screenIds.every((id) => selected.includes(id));
                  return (
                    <Tr key={item.key}>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <HStack spacing={2}>
                          <Text>{screenName(item.key)}</Text>
                          {item.isReviewOnly && (
                            <Badge colorScheme="green" variant="subtle" borderRadius="md" px={2}>
                              {t(`${BASIC_INFO_KEY}label.viewRestrictionReviewOnly`)}
                            </Badge>
                          )}
                          {usages && (
                            <HelpIcon size="16px" width="360px" header={t(`${BASIC_INFO_KEY}help.viewRestrictionUsageHeader`)}>
                              <Box>
                                {usages.map((usage) => (
                                  <Text key={usage.screenId}>
                                    {screenName(usage.screenId)}
                                    {usage.noteKey ? t(`${BASIC_INFO_KEY}label.${usage.noteKey}`) : ""}
                                  </Text>
                                ))}
                                <Text mt={2} fontSize="sm" color="gray.600">
                                  {t(`${BASIC_INFO_KEY}help.viewRestrictionUsageNote`)}
                                </Text>
                              </Box>
                            </HelpIcon>
                          )}
                        </HStack>
                      </Td>
                      <Td bgColor={isRestricted ? "red.50" : undefined}>
                        <HStack spacing={3}>
                          <Switch
                            colorScheme="blue"
                            isChecked={!isRestricted}
                            isDisabled={isReadOnly}
                            onChange={(e) => setRestricted(item.screenIds, !e.target.checked)}
                          />
                          <Text fontSize="sm" fontWeight="bold" color={isRestricted ? "red.600" : "gray.600"}>
                            {isRestricted
                              ? t(`${BASIC_INFO_KEY}label.viewRestrictionNotViewable`)
                              : t(`${BASIC_INFO_KEY}label.viewRestrictionViewable`)}
                          </Text>
                        </HStack>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
          <Text mt={4} fontSize="sm" color="gray.600">
            {t(`${BASIC_INFO_KEY}help.viewRestrictionSaveNotice`)}
          </Text>
        </ModalBody>
        <ModalFooter>
          {!isReadOnly && (
            <Button colorScheme="blue" ml={1} mr={1} onClick={() => onConfirm(serializeViewRestriction(selected))}>
              {t('common.button.confirmAction')}
            </Button>
          )}
          <Button colorScheme="gray" ml={1} mr={1} onClick={onClose}>
            {isReadOnly ? t('common.button.close') : t('common.button.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ViewRestrictionModal;
